// Copyright (c) Ping. All rights reserved.
//
// Fires device-local meeting reminders (§2.13). A meeting's `reminderMinutes`
// is the creator's per-device "remind me N minutes before" choice — it is NOT
// on the wire — so it lives in the local `meeting_reminders` table and whichever
// of the creator's tabs is awake at `startsAt - reminderMinutes*60_000` raises a
// notification. Mirrors `useReminderScheduler` (activity reminders): `firedAt`
// guards re-fire across reloads, `setTimeout` is capped at ~24.8 days and we
// re-arm on the next list change, and reminders already past their fire time on
// mount fire immediately (catch-up after the app was closed).
//
// Mount ONCE at the always-on shell so meeting reminders fire regardless of
// which route/panel is open.

import { useEffect } from 'react';

import { log } from '@core/log/log';
import { type MeetingReminder, webMeetingRemindersPersister } from '@core/persistence/db';

const MAX_TIMEOUT_MS = 2_147_483_647; // 2^31 - 1

function reminderLabel(minutes: number): string {
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  if (minutes < 1440) {
    const h = Math.round(minutes / 60);
    return `${h} hour${h === 1 ? '' : 's'}`;
  }
  const d = Math.round(minutes / 1440);
  return `${d} day${d === 1 ? '' : 's'}`;
}

function notify(title: string, body: string): void {
  try {
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      new Notification(title, { body });
      return;
    }
  } catch {
    /* fall through to log */
  }
  log.info({ event: 'meetingReminders.fire', title });
}

/** Watches the meeting-reminders store and fires each due reminder once. */
export function useMeetingReminderScheduler(): void {
  useEffect(() => {
    const timers = new Map<string, ReturnType<typeof setTimeout>>();
    let disposed = false;

    const fire = (r: MeetingReminder): void => {
      if (disposed) return;
      // Mark fired first (idempotent) so a re-projection doesn't re-arm it.
      void webMeetingRemindersPersister.put({ ...r, firedAt: Date.now() });
      notify(
        'Meeting reminder',
        `"${r.title}" starts in ${reminderLabel(r.reminderMinutes)}.`,
      );
    };

    const arm = (rows: readonly MeetingReminder[]): void => {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
      const now = Date.now();
      for (const r of rows) {
        if (r.firedAt !== undefined) continue; // already fired
        const fireAt = r.startsAt - r.reminderMinutes * 60_000;
        // Drop reminders whose meeting already started (nothing to remind about).
        if (r.startsAt <= now) continue;
        const delay = fireAt - now;
        if (delay <= 0) {
          fire(r);
        } else if (delay <= MAX_TIMEOUT_MS) {
          timers.set(
            r.meetingId,
            setTimeout(() => fire(r), delay),
          );
        }
        // Reminders > ~24 days out are re-armed on the next list change.
      }
    };

    const unsubscribe = webMeetingRemindersPersister.subscribe((rows) => arm(rows));

    return () => {
      disposed = true;
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
      unsubscribe();
    };
  }, []);
}
